/**
 * process-ag-coproprietes.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration des pages éditoriales /coproprietes/process-ag/[slug].
 *
 * Étapes du processus pour préparer, présenter et faire voter en
 * assemblée générale un projet de rénovation énergétique en copropriété.
 *
 * Sources juridiques :
 *  - Loi n° 65-557 du 10 juillet 1965 : statut de la copropriété
 *  - Loi Climat & Résilience du 22 août 2021, art. 171 : Plan
 *    Pluriannuel de Travaux (PPT)
 */

import type { ParcoursStub } from './parcours-particuliers';

export const PROCESS_AG_COPROPRIETES: ParcoursStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'note-convocation',
    iconName: 'newspaper',
    eyebrow: 'Loi du 10 juillet 1965',
    title: 'Note explicative pour la convocation AG',
    shortLabel: 'Note convocation',
    lead: "La note jointe à la convocation est le document clé pour faire voter votre projet de rénovation. Elle doit expliquer clairement le contexte technique, le plan de financement et ce qui est soumis au vote. Sa clarté conditionne le résultat.",
    badge: 'Pièce clé',
    badgeLabel: 'Conditionne le résultat du vote en AG',
    keyPoints: [
      {
        iconName: 'file-text',
        label: "Pédagogie d'abord",
        description: "Les copropriétaires votent rarement oui à ce qu'ils ne comprennent pas. La note doit rendre l'audit technique lisible pour tous.",
      },
      {
        iconName: 'wallet',
        label: 'Plan de financement clair',
        description: "Montant des travaux, aides obtenues, reste à charge pour la copropriété, quote-part par lot. Chaque copropriétaire doit voir ce qu'il paie.",
      },
      {
        iconName: 'gauge',
        label: 'Gain énergétique visible',
        description: "Amélioration de l'étiquette énergétique, économies de charges projetées, impact estimé sur la valeur du bien.",
      },
      {
        iconName: 'shield-check',
        label: 'Délai à respecter',
        description: "La note doit être envoyée au moins 21 jours avant l'AG avec la convocation. Un envoi à 30-35 jours est recommandé pour absorber les aléas.",
      },
    ],
    steps: [
      {
        title: 'Préparez avec le conseil syndical',
        description: "Rédaction conjointe avec le syndic et le conseil syndical : messages clés, arbitrages selon les sensibilités identifiées.",
      },
      {
        title: 'Validez le plan de financement',
        description: "Vérifiez les montants d'aides estimés et le prêt collectif envisagé à partir des devis obtenus.",
      },
      {
        title: 'Rédaction finale et annexes',
        description: "Note explicative + devis, audit énergétique, projet d'échéancier des appels de fonds. Vérifiez la formulation des résolutions.",
      },
      {
        title: 'Envoi avec la convocation',
        description: "Envoi par le syndic à chaque copropriétaire au moins 21 jours avant l'AG, par lettre recommandée ou voie électronique acceptée.",
      },
    ],
    faq: [
      {
        question: 'Quels documents joindre obligatoirement ?',
        answer:
          "Les devis détaillés, l'audit énergétique, le plan de financement et toutes les pièces utiles pour éclairer le vote. La liste exacte dépend de ce qui est soumis au vote.",
      },
      {
        question: 'Le syndic peut-il rédiger seul la note ?',
        answer:
          "Oui, c'est sa mission. Mais une rédaction conjointe avec le conseil syndical améliore considérablement la qualité et l'adhésion des copropriétaires. Les chances de succès sont bien meilleures.",
      },
      {
        question: 'Et si la note est envoyée en retard ?',
        answer:
          "Le non-respect du délai de 21 jours peut entraîner l'annulation de la résolution sur recours d'un copropriétaire opposant. Mieux vaut viser 30-35 jours d'avance.",
      },
    ],
    ctaLabel: 'Cadrer ma convocation AG',
    ctaHref: '/contact?source=coproprietes&besoin=convocation-ag',
    secondaryCta: { label: 'Voir la résolution AG', href: '/coproprietes/process-ag/resolution' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'resolution',
    iconName: 'file-signature',
    eyebrow: 'Articles 24, 25, 25-1',
    title: 'Projet de résolution AG',
    shortLabel: 'Résolution AG',
    lead: "Le projet de résolution est le texte exact soumis au vote. Chaque mot détermine la majorité applicable, la sécurité du vote et les pouvoirs du syndic pour exécuter les décisions.",
    badge: 'Sécurité juridique',
    badgeLabel: 'Choix de majorité décisif',
    keyPoints: [
      {
        iconName: 'file-signature',
        label: 'Une résolution = un objet',
        description: "Travaux, choix du prestataire, mandat au syndic, prêt collectif, appels de fonds : chaque sujet mérite une résolution distincte.",
      },
      {
        iconName: 'gavel',
        label: 'Bien choisir la majorité',
        description: "Majorité simple (voix des présents/représentés), majorité absolue (toutes les voix du syndicat) ou majorité renforcée selon l'objet du vote.",
      },
      {
        iconName: 'handshake',
        label: 'Mandat clair au syndic',
        description: "Mandat précis pour signer les marchés, déposer les dossiers d'aides, contracter l'emprunt collectif, encaisser les primes et les affecter aux appels de fonds.",
      },
      {
        iconName: 'shield-check',
        label: 'Protection contre les recours',
        description: "Une rédaction soignée limite les risques de contestation dans le délai légal de 2 mois suivant l'AG.",
      },
    ],
    steps: [
      {
        title: 'Listez les résolutions nécessaires',
        description: "Approbation des travaux, choix de l'entreprise, recours au prêt collectif, appels de fonds, mandat au syndic.",
      },
      {
        title: 'Déterminez la majorité applicable',
        description: "Analysez chaque résolution pour identifier la majorité requise. Une erreur peut rendre le vote inopérant ou attaquable.",
      },
      {
        title: 'Rédigez le texte',
        description: "Formulation précise de chaque résolution. Prévoyez la solution de repli si la majorité absolue n'est pas atteinte.",
      },
      {
        title: 'Faites relire',
        description: "Validation par le conseil syndical et, pour les opérations importantes, par un juriste spécialisé. Sécurise contre les recours.",
      },
    ],
    faq: [
      {
        question: 'Majorité simple ou absolue : quelle différence ?',
        answer:
          "La majorité simple (voix des présents et représentés) régit l'entretien courant. La majorité absolue (toutes les voix du syndicat) régit les travaux d'amélioration, dont la plupart des opérations d'économies d'énergie. Une passerelle existe si la majorité absolue n'est pas atteinte mais qu'au moins un tiers des voix s'est exprimé pour.",
      },
      {
        question: 'Peut-on regrouper plusieurs résolutions ?',
        answer:
          "Non, c'est fortement déconseillé et peut entraîner la nullité. Chaque résolution doit porter sur un objet précis et faire l'objet d'un vote distinct. Un projet de rénovation appelle typiquement 4 à 8 résolutions.",
      },
      {
        question: 'Faut-il joindre les devis ?',
        answer:
          "Oui, lorsque la résolution porte sur l'approbation des travaux et le choix d'une entreprise. Pour les opérations importantes, vous pouvez demander un mandat au syndic pour la mise en concurrence, avec ratification en AG suivante.",
      },
    ],
    ctaLabel: 'Sécuriser ma résolution',
    ctaHref: '/contact?source=coproprietes&besoin=resolution-ag',
    secondaryCta: { label: 'Stratégie de vote', href: '/coproprietes/process-ag/vote' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'vote',
    iconName: 'gavel',
    eyebrow: '1ère vs 2nde convocation',
    title: 'Stratégie de vote en AG',
    shortLabel: 'Vote AG',
    lead: "Faire voter des travaux en AG se prépare en amont. Mobiliser les copropriétaires, anticiper les objections, maîtriser la mécanique de la seconde convocation : c'est ce qui fait la différence entre un projet adopté et un projet repoussé.",
    badge: 'Préparation amont',
    badgeLabel: "La réussite se joue avant l'AG",
    keyPoints: [
      {
        iconName: 'users',
        label: 'Mobilisez les votes',
        description: "Collectez les pouvoirs en amont, identifiez les opposants, organisez des réunions d'information avant l'AG.",
      },
      {
        iconName: 'gavel',
        label: 'Maîtrisez les règles de vote',
        description: "Si la majorité absolue n'est pas atteinte mais qu'au moins un tiers des voix s'est exprimé pour, un second vote immédiat peut être tenté à la majorité simple.",
      },
      {
        iconName: 'shield-check',
        label: 'Anticipez les objections',
        description: "Préparez des réponses claires sur le coût, la quote-part, l'impact sur la valeur du bien et les nuisances du chantier.",
      },
      {
        iconName: 'file-text',
        label: 'Procès-verbal sécurisé',
        description: "Un PV précis, notifié dans le mois suivant l'AG, limite les risques de contestation et fixe les bases pour l'exécution.",
      },
    ],
    steps: [
      {
        title: 'Réunion préparatoire du conseil syndical',
        description: "Définissez la stratégie : ordre des résolutions, scénario de repli, gestion des opposants connus.",
      },
      {
        title: "Réunion d'information pré-AG",
        description: "Présentez le projet aux copropriétaires hors AG. Répondez aux questions, collectez les pouvoirs des absents.",
      },
      {
        title: "Tenez l'AG",
        description: "Présentez les résolutions, animez le débat, votez selon les majorités requises, activez la solution de repli si nécessaire.",
      },
      {
        title: 'PV et notifications',
        description: "Rédigez le PV, notifiez les opposants et absents dans le mois suivant l'AG. C'est le point de départ du délai de recours de 2 mois.",
      },
    ],
    faq: [
      {
        question: "Que faire si la majorité absolue n'est pas atteinte ?",
        answer:
          "Si la résolution recueille au moins un tiers des voix de tous les copropriétaires, vous pouvez immédiatement procéder à un second vote à la majorité simple des présents et représentés. C'est un mécanisme clé pour faire passer les travaux.",
      },
      {
        question: 'Et la seconde convocation ?',
        answer:
          "Si l'AG ne peut pas délibérer valablement, une seconde AG est convoquée. Certaines résolutions y bénéficient d'une majorité allégée, ce qui peut servir votre stratégie.",
      },
      {
        question: 'Le vote par correspondance est-il possible ?',
        answer:
          "Oui, via un formulaire joint à la convocation. Il permet aux absents de voter sans donner pouvoir. Le syndic doit le comptabiliser et le PV doit en faire mention.",
      },
    ],
    ctaLabel: 'Cadrer ma stratégie de vote',
    ctaHref: '/contact?source=coproprietes&besoin=strategie-vote-ag',
    secondaryCta: { label: 'Voir le PPT', href: '/coproprietes/process-ag/ppt' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'ppt',
    iconName: 'clipboard-list',
    eyebrow: 'Loi Climat & Résilience 2021',
    title: 'Plan Pluriannuel de Travaux (PPT)',
    shortLabel: 'PPT',
    lead: "Le PPT est obligatoire pour les copropriétés de plus de 15 ans ou de 50 lots et plus. C'est le cadre qui planifie les travaux sur 10 ans et facilite l'accès aux financements.",
    badge: '10 ans',
    badgeLabel: 'Cadre stratégique de travaux',
    keyPoints: [
      {
        iconName: 'clipboard-list',
        label: 'Une obligation légale',
        description: "Toutes les copropriétés concernées (≥ 50 lots ou plus de 15 ans) doivent établir un PPT. L'entrée en vigueur est progressive selon la taille.",
      },
      {
        iconName: 'file-search',
        label: "Basé sur le diagnostic collectif",
        description: "Le PPT s'appuie sur l'étiquette énergétique de l'immeuble, obligatoire pour les mêmes copropriétés.",
      },
      {
        iconName: 'wallet',
        label: 'Alimente le fonds travaux',
        description: "Le PPT cadre l'usage du fonds travaux obligatoire. Il permet d'étaler la charge dans le temps plutôt que de tout appeler d'un coup.",
      },
      {
        iconName: 'gauge',
        label: "Facilite l'accès aux aides",
        description: "Un PPT bien élaboré sert de socle aux dossiers de demande d'aides pour les rénovations programmées.",
      },
    ],
    steps: [
      {
        title: "Vérifiez l'obligation",
        description: "Taille de la copropriété, âge de l'immeuble, échéance d'entrée en vigueur selon le calendrier applicable.",
      },
      {
        title: 'Choisissez un professionnel',
        description: "Le PPT doit être élaboré par un architecte, un bureau d'études ou un diagnostiqueur certifié.",
      },
      {
        title: 'Élaborez le PPT',
        description: "Diagnostic technique global, hiérarchisation des travaux sur 10 ans, estimation des coûts, échéancier indicatif.",
      },
      {
        title: 'Présentez et faites voter',
        description: "Le PPT doit être présenté à la première AG suivant son élaboration. L'AG peut adopter un plan d'action et programmer les premiers travaux.",
      },
    ],
    faq: [
      {
        question: 'Qui est concerné par le PPT ?',
        answer:
          "Toutes les copropriétés d'habitation dont l'immeuble a plus de 15 ans ou qui comptent 50 lots et plus. L'entrée en vigueur est progressive : d'abord les copropriétés de 200 lots et plus.",
      },
      {
        question: 'PPT et diagnostic collectif : quelle différence ?',
        answer:
          "Le diagnostic collectif donne l'étiquette énergétique de l'immeuble. Le PPT est le plan d'action sur 10 ans : il s'appuie sur ce diagnostic pour prioriser les travaux énergétiques, mais couvre aussi la sécurité et le gros entretien.",
      },
      {
        question: 'Le PPT oblige-t-il à réaliser les travaux ?',
        answer:
          "Non, le PPT ne contraint pas directement à exécuter les travaux. Son adoption en AG dépend des majorités habituelles. Mais son absence peut peser dans certains contentieux.",
      },
    ],
    ctaLabel: 'Cadrer mon PPT',
    ctaHref: '/contact?source=coproprietes&besoin=ppt',
    secondaryCta: { label: 'Voir le diagnostic', href: '/coproprietes/diagnostic-cee' },
  },
];

export function getProcessAgCopropriete(slug: string): ParcoursStub | undefined {
  return PROCESS_AG_COPROPRIETES.find((p) => p.slug === slug);
}
